import { FC, useEffect, useRef, useState } from "react";
import {
  ClienteAutocomplete,
  clienteOptionLabel,
  searchClientesAutocomplete,
} from "../utils/cardapio.api";

interface Props {
  id: string;
  label?: string;
  value: ClienteAutocomplete | null;
  onChange: (cliente: ClienteAutocomplete | null) => void;
  disabled?: boolean;
  placeholder?: string;
  /** Texto auxiliar abaixo do campo (ex.: pedido em aberto). */
  hint?: string;
  className?: string;
  minChars?: number;
}

/**
 * Autocomplete de cliente via `clientes_v` (nome, CPF, pulseira vinculada).
 * Usado na página /cardapio/pedido e no popup Novo Pedido do topo.
 */
export const ClienteAutocompleteField: FC<Props> = ({
  id,
  label = "Cliente (Nome/CPF/Pulseira)",
  value,
  onChange,
  disabled = false,
  placeholder = "Digite ao menos 3 letras (nome, CPF ou pulseira)...",
  hint,
  className = "",
  minChars = 3,
}) => {
  const [query, setQuery] = useState(() =>
    value ? clienteOptionLabel(value) : "",
  );
  const [options, setOptions] = useState<ClienteAutocomplete[]>([]);
  const [loading, setLoading] = useState(false);
  const [showDrop, setShowDrop] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (value) {
      setQuery(clienteOptionLabel(value));
    } else if (!query) {
      setQuery("");
    }
    // Sync label when parent clears/sets selection externally.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    const term = query.trim();

    if (value && clienteOptionLabel(value) === query) {
      setOptions([]);
      return;
    }

    if (term.length < minChars) {
      setOptions([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    timer.current = setTimeout(() => {
      searchClientesAutocomplete(term)
        .then((list) => {
          setOptions(list);
          setShowDrop(true);
        })
        .catch(() => setOptions([]))
        .finally(() => setLoading(false));
    }, 300);

    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [query, value, minChars]);

  return (
    <div className={`position-relative ${className}`.trim()}>
      <label className="form-label required" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        type="text"
        className="form-control form-control-sm"
        value={query}
        autoComplete="off"
        placeholder={placeholder}
        disabled={disabled}
        onFocus={() => {
          if (options.length) setShowDrop(true);
        }}
        onBlur={() => {
          setTimeout(() => setShowDrop(false), 150);
        }}
        onChange={(e) => {
          setQuery(e.target.value);
          if (value) onChange(null);
        }}
      />
      {loading && <div className="form-text">Buscando clientes...</div>}
      {hint ? <div className="form-text text-primary">{hint}</div> : null}
      {showDrop && options.length > 0 && (
        <ul
          className="list-group position-absolute w-100 shadow-sm"
          style={{ zIndex: 1055, maxHeight: 220, overflowY: "auto" }}
        >
          {options.map((c) => (
            <li key={`${c.cliente_id}-${c.pulseira_conteudo || c.cpf || ""}`}>
              <button
                type="button"
                className="list-group-item list-group-item-action text-start"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onChange(c);
                  setQuery(clienteOptionLabel(c));
                  setShowDrop(false);
                  setOptions([]);
                }}
              >
                {clienteOptionLabel(c)}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
